import axios from "axios";

import { useToast } from "vue-toastification";

import { getMailProviderFromHost, oauthProviderMap } from "./utils";

import type { MailServer, MailProvider } from "./types";

export const useMailServerStore = defineStore('mail-server', () => {
    const toast = useToast();

    const mailServer = ref<MailServer | null>();

    const hasMailServer = computed<boolean>(() => !!mailServer.value);

    const isLoaded = ref<boolean>(false);

    /**
     * Load the mail server configuration for the authenticated user.
     */
    const load = async (): Promise<void> => {
        if(isLoaded.value) return;

        try {
            const response = await axios.get<{
                mailServer: MailServer | null
            }>('/api/mailserver/config');

            mailServer.value = response.data.mailServer;

            if(!mailServer.value) return;

            mailServer.value.provider = getMailProviderFromHost(mailServer.value.host);
        }
        catch(error) {
            console.error(error);

            toast.error("Es konnte keine Verbindung zum Mailserver hergestellt werden");
        }
        finally {
            isLoaded.value = true;
        }
    };

    /**
     * Save or update the mail server configuration.
     *
     * @param _mailServer The mail server data to save
     * @returns True if saved successfully
     */
    const save = async (_mailServer: MailServer): Promise<boolean> => {
        try {
            const { data } = await axios.post<MailServer>('/api/mailserver/config', _mailServer);

            mailServer.value = data;

            mailServer.value.provider = getMailProviderFromHost(mailServer.value.host);

            toast.success("MailServer erfolgreich aktualisiert");

            return true;
        }
        catch(error) {
            console.error(error);

            toast.error("Es konnte keine Verbindung zum Mailserver hergestellt werden");

            return false;
        }
    };

    /**
     * Test the connection to the configured mail server.
     *
     * @returns True if the connection test succeeded
     */
    const test = async (): Promise<boolean> => {
        try {
            const { data } = await axios.post<{
                error_code: string | null,
                mailServer: MailServer
            }>('/api/mailserver/test');

            mailServer.value = data.mailServer;

            mailServer.value.provider = getMailProviderFromHost(mailServer.value.host);

            if(data.error_code) {
                throw new Error(data.error_code);
            }

            toast.success("Verbindung erfolgreich");

            return true;
        }
        catch(error) {
            console.error(error);

            toast.error("Es konnte keine Verbindung zum Mailserver hergestellt werden");

            return false;
        }
    };

    /**
     * Remove the configured mail server.
     *
     * @returns True if removed successfully
     */
    const remove = async (): Promise<boolean> => {
        try {
            await axios.delete('/api/mailserver/config');

            toast.success("MailServer erfolgreich entfernt");

            mailServer.value = null;

            return true;
        }
        catch(error) {
            console.error(error);

            toast.error("Fehler beim Entfernen des Mailservers");

            return false;
        }
    };

    /**
     * Get the OAuth2 authorization URL for the given provider.
     *
     * @param provider The frontend provider name (e.g. 'microsoft')
     * @param fromName The display name for outgoing emails
     * @returns The authorization URL to open in a popup, or null on failure
     */
    const getOAuthUrl = async (provider: MailProvider, fromName: string): Promise<string | null> => {
        const oauthProvider = oauthProviderMap[provider];

        if(!oauthProvider) {
            toast.error("OAuth wird für diesen Anbieter nicht unterstützt");
            return null;
        }

        try {
            const { data } = await axios.get<{ authorization_url: string }>(
                `/api/oauth/mail/${oauthProvider}/authorize`,
                { params: { from_name: fromName } }
            );

            return data.authorization_url;
        }
        catch(error) {
            console.error(error);

            toast.error("Fehler beim Starten der OAuth-Verbindung");

            return null;
        }
    };

    /**
     * Reload the mail server config after a successful OAuth connection.
     *
     * @returns True if the mail server is now connected via OAuth
     */
    const reloadAfterOAuth = async (): Promise<boolean> => {
        isLoaded.value = false;

        await load();

        if(mailServer.value?.oauth_connected) {
            toast.success("MailServer erfolgreich verbunden");
            return true;
        }

        toast.error("Verbindung fehlgeschlagen. Bitte versuche es erneut.");
        return false;
    };

    /**
     * Reset the store to its initial state, so the next account loads its own mail server.
     */
    const reset = (): void => {
        mailServer.value = undefined;
        isLoaded.value = false;
    };

    return { mailServer, hasMailServer, isLoaded, load, save, remove, test, getOAuthUrl, reloadAfterOAuth, reset };
});
